import { willLoginAtomic } from 'fe-comp-aperture';
import { isAppLoaded, mountApp } from 'fe-lib-async-app';
import { logError } from 'fe-lib-logging';

const PATH = '/wisdom';
const APP_NAME = 'hs-app-wisdom';
const ROOT_ID = 'wisdomStandaloneRoot';

const NO_APERTURE_SESSION_MESSAGE = 'NO_APERTURE_SESSION';

function readPagePayloadFromRootNode(node) {
    try {
        const raw =
            node.getAttribute('wisdomStandalonePageJsonPayload') || '{}';
        return JSON.parse(raw);
    } catch (_e) {
        return {};
    }
}

function resolveRouterBasePath(pagePayload) {
    const basePath =
        pagePayload && typeof pagePayload.basePath === 'string'
            ? pagePayload.basePath.trim()
            : '';
    return basePath || PATH;
}

async function hasActiveSession() {
    try {
        const url = new URL(window.location.href);

        const { success } = await willLoginAtomic({
            hostname: url.hostname,
            resource: url.pathname,
            dropCookie: false,
        });

        return success;
    } catch (_e) {
        return false;
    }
}

function notifyParentNoSession() {
    if (window.parent === window) {
        return;
    }

    window.parent.postMessage(
        {
            type: NO_APERTURE_SESSION_MESSAGE,
        },
        '*',
    );
}

/** Full-viewport mount target — no dashboard secondary canvas. */
function prepareStandaloneRoot(node) {
    Object.assign(node.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
    });
}

async function mountWisdomApp(container, pagePayload, basePath) {
    return mountApp({
        appName: APP_NAME,
        path: basePath,
        mount: (app) => {
            app.mount(container, {
                dto: pagePayload,
                basePath,
                isStandalone: true,
            });
            return Promise.resolve({ app, node: container });
        },
    }).catch((e) => {
        logError(
            'hs_app_wisdom_standalone',
            'Wisdom standalone async app failed to load',
            {
                errorMessage: JSON.stringify(e.message),
                stack: JSON.stringify(e.stack),
            },
        );

        throw e;
    });
}

export default {
    load: async () => {
        if (isAppLoaded(APP_NAME)) {
            return;
        }

        const hasSession = await hasActiveSession();
        if (!hasSession) {
            notifyParentNoSession();
        }

        const container = document.getElementById(ROOT_ID);
        if (!container) {
            throw new Error(`[${APP_NAME}] Missing root element #${ROOT_ID}`);
        }

        const pagePayload = readPagePayloadFromRootNode(container);
        const basePath = resolveRouterBasePath(pagePayload);
        prepareStandaloneRoot(container);

        await mountWisdomApp(container, pagePayload, basePath);
    },
};
